import { Button, Drawer, Dropdown, Grid, Layout, Menu, Tooltip, Typography } from 'antd';
import {
  Logout01Icon,
  Menu01Icon,
  Moon02Icon,
  Sun01Icon,
  UserIcon,
} from '@hugeicons/core-free-icons';
import { useCallback, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PolicyPrompt } from '@/features/policies/PolicyPrompt.js';
import { FaceSetupPrompt } from '@/features/attendance/face/FaceSetupPrompt.js';
import { NotificationBell } from '@/features/notifications/NotificationBell.js';
import { useCloseOnScroll } from '@/lib/ui/close-on-scroll.js';
import { Icon } from '../Icon/Icon.js';
import { PersonAvatar } from '../PersonAvatar/PersonAvatar.js';
import { NAV_SECTIONS, isPermitted } from './navigation.js';
import { useAuth } from '@/lib/auth/AuthProvider.js';
import { useTheme } from '@/design/ThemeProvider.js';
import { layout as layoutTokens, neutral, space, typography, zIndex } from '@/design/tokens.js';

const { Sider, Header, Content } = Layout;
const { Text } = Typography;

/**
 * The application frame: sidebar, top bar, content.
 *
 * Responsive behaviour is a real breakpoint change, not a squeeze. Below
 * `lg` the sidebar becomes an overlay drawer that closes on navigation,
 * because a permanently visible 248px sidebar on a phone leaves no room for
 * the content it navigates to.
 *
 * The sidebar is a `<nav>`, the content is `<main id="main">`, and a skip link
 * jumps between them — so a keyboard user is not forced through twenty nav
 * items to reach the page.
 */
export function AppShell() {
  const screens = Grid.useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, session, signOut, canAny, can } = useAuth();
  const managesPeople = session?.managesPeople ?? false;
  const { mode, setPreference } = useTheme();

  const isCompact = !screens.lg;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // The panel is positioned once, against a button that the page can scroll
  // out from under it (orbit-ui#179).
  useCloseOnScroll(
    accountOpen,
    useCallback(() => setAccountOpen(false), []),
  );

  /**
   * The 64px icon-only rail. Not the same thing as `collapsed`: below `lg`
   * the sidebar is a full-width drawer, where `collapsed` means nothing.
   */
  const isRail = collapsed && !isCompact;

  /**
   * Drop items the user cannot use, then drop sections left empty. Done here
   * rather than in each item's render so an entire section heading with no
   * items beneath it can never appear.
   */
  const visibleSections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => isPermitted(item, { can, canAny, managesPeople })),
      })).filter((section) => section.items.length > 0),
    [can, canAny, managesPeople],
  );

  /**
   * Longest matching path wins, so `/team/approvals` highlights Approvals
   * rather than Team. Sorting by length is what stops a parent route stealing
   * the highlight from its own child.
   */
  const selectedKey = useMemo(() => {
    const all = visibleSections.flatMap((section) => section.items);
    const matches = all
      .filter((item) =>
        item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to),
      )
      .sort((a, b) => b.to.length - a.to.length);
    return matches[0]?.key ?? '';
  }, [visibleSections, location.pathname]);

  const navigation = (
    <nav aria-label="Main" style={{ paddingBlock: space[2] }}>
      {visibleSections.map((section, index) => (
        <div key={section.title ?? `section-${index}`} style={{ marginBottom: space[2] }}>
          {section.title ? (
            <>
              {/*
                In the rail the heading is hidden from sight but kept for a
                screen reader, which still needs the grouping. Left visible it
                wraps "Administration" down five lines of a 64px column — the
                heading is the one part of the sidebar antd does not collapse
                for us, because it is ours and not a menu item.
              */}
              <Text
                type="secondary"
                className={isRail ? 'orbit-visually-hidden' : undefined}
                style={{
                  display: 'block',
                  padding: `${space[2]}px ${space[4]}px ${space[1]}px`,
                  fontSize: typography.size.xs,
                  fontWeight: typography.weight.semibold,
                  letterSpacing: typography.letterSpacing.caps,
                  textTransform: 'uppercase',
                }}
              >
                {section.title}
              </Text>
              {/* Something still has to separate the groups once the words go. */}
              {isRail && index > 0 ? (
                <div
                  aria-hidden
                  style={{
                    height: 1,
                    margin: `${space[2]}px ${space[3]}px`,
                    background: 'var(--ant-color-border-secondary)',
                  }}
                />
              ) : null}
            </>
          ) : null}

          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            style={{ border: 'none', background: 'transparent' }}
            items={section.items.map((item) => ({
              key: item.key,
              icon: <Icon icon={item.icon} size="md" />,
              label: <Link to={item.to}>{item.label}</Link>,
              // What the tooltip says in the rail. Without it antd would put
              // the <Link> element itself in the tooltip.
              title: item.label,
            }))}
            onClick={({ key, domEvent }) => {
              setDrawerOpen(false);

              // The label is a <Link>, and in the rail antd collapses it to
              // `width: 0; opacity: 0`. The anchor is still in the DOM but
              // nothing can land on it, so every click hits the <li> and the
              // rail navigates nowhere. Navigate from the menu when the click
              // missed the anchor, and stand back when it did not so the Link
              // does not push the same entry onto history twice.
              if ((domEvent.target as HTMLElement).closest('a')) return;
              const target = section.items.find((item) => item.key === key);
              if (target) navigate(target.to);
            }}
          />
        </div>
      ))}
    </nav>
  );

  const brand = (
    <Link
      to="/"
      style={{
        display: 'flex',
        alignItems: 'center',
        // Centred in the rail, where 16px of padding either side of a 28px
        // badge leaves it visibly off-centre in a 64px column.
        justifyContent: isRail ? 'center' : 'flex-start',
        gap: space[2],
        padding: isRail ? 0 : `0 ${space[4]}px`,
        height: layoutTokens.topbarHeight,
        color: 'inherit',
        textDecoration: 'none',
        flexShrink: 0,
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'grid',
          placeItems: 'center',
          width: 28,
          height: 28,
          borderRadius: 7,
          background: `linear-gradient(135deg, var(--ant-color-primary) 0%, var(--ant-color-primary-active) 100%)`,
          color: neutral[0],
          fontWeight: typography.weight.bold,
          fontSize: typography.size.sm,
          letterSpacing: typography.letterSpacing.tight,
        }}
      >
        O
      </span>
      {!isRail && (
        <span
          style={{
            fontWeight: typography.weight.semibold,
            fontSize: typography.size.md,
            letterSpacing: typography.letterSpacing.tight,
          }}
        >
          Orbit
        </span>
      )}
    </Link>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <a className="orbit-skip-link" href="#main">
        Skip to content
      </a>

      {isCompact ? (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="left"
          size={layoutTokens.sidebarWidth}
          closable={false}
          styles={{ body: { padding: 0 } }}
        >
          {brand}
          {navigation}
        </Drawer>
      ) : (
        <Sider
          className="orbit-sidebar"
          width={layoutTokens.sidebarWidth}
          collapsedWidth={layoutTokens.sidebarCollapsedWidth}
          collapsed={collapsed}
          theme="light"
          style={{
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            borderInlineEnd: `1px solid var(--ant-color-border-secondary)`,
            background: 'var(--ant-color-bg-container)',
            zIndex: zIndex.sidebar,
          }}
        >
          {brand}
          {navigation}
        </Sider>
      )}

      <Layout style={{ background: 'var(--ant-color-bg-layout)' }}>
        <Header
          className="orbit-topbar"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: zIndex.topbar,
            height: layoutTokens.topbarHeight,
            lineHeight: `${layoutTokens.topbarHeight}px`,
            display: 'flex',
            alignItems: 'center',
            gap: space[2],
            paddingInline: space[4],
            background: 'var(--ant-color-bg-container)',
            borderBottom: `1px solid var(--ant-color-border-secondary)`,
          }}
        >
          <Button
            type="text"
            aria-label={
              isCompact ? 'Open navigation' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'
            }
            aria-expanded={isCompact ? drawerOpen : !collapsed}
            icon={<Icon icon={Menu01Icon} size="lg" />}
            onClick={() => (isCompact ? setDrawerOpen(true) : setCollapsed((value) => !value))}
          />

          <div style={{ flex: 1 }} />

          <Tooltip title={mode === 'dark' ? 'Switch to light' : 'Switch to dark'}>
            <Button
              type="text"
              aria-label={mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              icon={<Icon icon={mode === 'dark' ? Sun01Icon : Moon02Icon} size="lg" />}
              onClick={() => setPreference(mode === 'dark' ? 'light' : 'dark')}
            />
          </Tooltip>

          <NotificationBell />

          {/* Controlled, so it can be closed when the page moves under it
              (orbit-ui#179). An uncontrolled Dropdown has no way to be told
              anything. */}
          <Dropdown
            open={accountOpen}
            onOpenChange={setAccountOpen}
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'identity',
                  label: (
                    <div style={{ paddingBlock: space[1] }}>
                      <div style={{ fontWeight: typography.weight.medium }}>{user?.fullName}</div>
                      <Text type="secondary" style={{ fontSize: typography.size.sm }}>
                        {user?.employeeNo}
                      </Text>
                    </div>
                  ),
                  disabled: true,
                },
                { type: 'divider' },
                {
                  key: 'profile',
                  icon: <Icon icon={UserIcon} size="sm" />,
                  label: 'My profile',
                  onClick: () => {
                    setAccountOpen(false);
                    void navigate('/profile');
                  },
                },
                {
                  key: 'signout',
                  icon: <Icon icon={Logout01Icon} size="sm" />,
                  label: 'Sign out',
                  danger: true,
                  // Navigate explicitly rather than relying on the route
                  // guard noticing. Sign-out must land the user somewhere
                  // safe even if a re-render is delayed.
                  onClick: () => {
                    void signOut().then(() => navigate('/sign-in', { replace: true }));
                  },
                },
              ],
            }}
          >
            <Button type="text" aria-label="Account menu" style={{ paddingInline: space[1] }}>
              <PersonAvatar fullName={user?.fullName ?? ''} photoUrl={user?.photoUrl} size={28} />
            </Button>
          </Dropdown>
        </Header>

        <Content
          id="main"
          className="orbit-content"
          style={{
            padding: isCompact ? space[4] : space[6],
            maxWidth: layoutTokens.contentMaxWidth,
            width: '100%',
            marginInline: 'auto',
          }}
        >
          {/*
            Above the page, not over it. Face check-in is required in order
            to check in, so the reminder has to find somebody wherever they
            are — but somebody who has not scanned yet can still read an
            announcement and book leave, so it does not block the app the
            way a mandatory policy does.
          */}
          <FaceSetupPrompt />
          <Outlet />
        </Content>

        {/*
          Mounted in the shell rather than per page: a mandatory policy has to
          stop somebody wherever they are, and a prompt wired into one screen
          only asks the people who happen to visit it.
        */}
        <PolicyPrompt />
      </Layout>
    </Layout>
  );
}
